import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useTheme } from "../config/ThemeContext";
import { Link, useNavigate } from "react-router-dom";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // null = still checking URL, true = valid token found, false = invalid/missing
  const [isValidToken, setIsValidToken] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const colors = useTheme();
  const tokenCheckedRef = useRef(false);

  useEffect(() => {
    // Prevent React 18 StrictMode double-invocation from overwriting state
    // after replaceState removes hash/search on the first run
    if (tokenCheckedRef.current) return;
    tokenCheckedRef.current = true;

    const hash = window.location.hash;
    const search = window.location.search;

    const hashParams = hash ? new URLSearchParams(hash.substring(1)) : null;
    const queryParams = search ? new URLSearchParams(search) : null;

    const getParam = (key) => hashParams?.get(key) || queryParams?.get(key);

    // 1. Check for Supabase error parameters
    const error = getParam("error");
    const errorCode = getParam("error_code");
    const errorDescription = getParam("error_description");

    if (error) {
      const desc = errorDescription || error;
      if (errorCode === "otp_expired") {
        setErrorMsg("This reset link has expired or has already been used. Please request a new one.");
      } else {
        setErrorMsg(decodeURIComponent(desc.replace(/\+/g, " ")));
      }
      setIsValidToken(false);
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    // 2. Check for access token (Supabase recovery redirect)
    const token = getParam("access_token");
    const type = getParam("type");

    if (token && (type === "recovery" || !type)) {
      setAccessToken(token);
      setIsValidToken(true);
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    // 3. Fallback check for PKCE code
    const code = getParam("code");
    if (code) {
      setErrorMsg("Authentication code received, but direct recovery token is required.");
      setIsValidToken(false);
      window.history.replaceState(null, "", window.location.pathname);
      return;
    }

    // 4. Nothing found in URL
    setErrorMsg("No reset token found in the URL. The link may be incomplete or invalid.");
    setIsValidToken(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setErrorMsg("");

    if (newPassword.length < 6) {
      setErrorMsg("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("Passwords do not match");
      return;
    }

    if (!accessToken) {
      setErrorMsg("Invalid or expired reset link");
      setIsValidToken(false);
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/reset-password`,
        { accessToken, newPassword }
      );

      setMessage(response.data.message);

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      if (err.response?.status === 400) {
        setErrorMsg(err.response?.data?.error || "Invalid or expired reset link");
        setIsValidToken(false);
      } else {
        setErrorMsg(
          err.response?.data?.error || "Failed to reset password. Please try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Still checking the URL — show a neutral loading state
  if (isValidToken === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-sm text-center">
          <img
            src="/assets/logos/volleyfly-logo-300x111.png"
            alt="VolleyFly"
            className="mx-auto pb-4"
          />
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mt-4"></div>
          <p className="text-gray-500 text-sm mt-3">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-sm">
          <img
            src="/assets/logos/volleyfly-logo-300x111.png"
            alt="VolleyFly"
            className="mx-auto pb-4"
          />
          <h2 className="text-xl font-bold text-center mb-2">Invalid Reset Link</h2>
          <p className="text-gray-600 text-sm text-center mb-6">
            {errorMsg || "This password reset link is invalid or has expired."}
          </p>
          <div className="mt-6">
            <p className="text-center text-gray-600 text-sm">
              <Link
                to="/request-password-reset"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Request a new reset link
              </Link>
            </p>
          </div>
          <div className="mt-4">
            <p className="text-center text-gray-600 text-sm">
              <Link
                to="/login"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Back to Login
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-sm">
        <img
          src="/assets/logos/volleyfly-logo-300x111.png"
          alt="VolleyFly"
          className="mx-auto pb-4"
        />
        <h2 className="text-xl font-bold text-center mb-2">Set New Password</h2>
        <p className="text-gray-600 text-sm text-center mb-6">
          Enter your new password below.
        </p>

        {message && (
          <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg text-sm">
            {message}
            <p className="mt-1">Redirecting to login...</p>
          </div>
        )}

        {errorMsg && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            className="border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            className="border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full ${colors.primary.bg} text-white font-semibold py-2 rounded-lg hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </button>
        </form>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <p className="text-center text-gray-600 text-sm">
            <Link
              to="/login"
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              Back to Login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
